import React from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { PageHeaderCard } from "@/components/layout/PageHeaderCard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Building2,
  Users,
  CreditCard,
  Settings,
  Shield,
  BarChart3,
  ArrowRight,
  Activity,
  Database,
  Bell,
  MessageSquare,
} from "lucide-react";

type ControlModule = {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  route?: string;
  status: "available" | "planned";
  category: "Operação" | "Financeiro" | "Segurança" | "Sistema";
};

const modules: ControlModule[] = [
  {
    title: "Dashboard Executivo",
    description: "Métricas globais do sistema, empresas e receita recorrente.",
    icon: BarChart3,
    route: "/admin/dashboard",
    status: "available",
    category: "Operação",
  },
  {
    title: "Empresas",
    description: "Cadastro, edição e acompanhamento de empresas ativas.",
    icon: Building2,
    route: "/admin/companies",
    status: "available",
    category: "Operação",
  },
  {
    title: "Usuários e Permissões",
    description: "Gestão de usuários e concessão/revogação de role admin.",
    icon: Users,
    route: "/admin/users",
    status: "available",
    category: "Segurança",
  },
  {
    title: "Planos",
    description: "Configuração de planos, limites e disponibilidade comercial.",
    icon: CreditCard,
    route: "/admin/plans",
    status: "available",
    category: "Financeiro",
  },
  {
    title: "Cobranças",
    description: "Monitoramento de assinaturas, status e vencimentos próximos.",
    icon: Activity,
    route: "/admin/billing",
    status: "available",
    category: "Financeiro",
  },
  {
    title: "Configurações Administrativas",
    description: "Administração de acessos administrativos e parâmetros globais.",
    icon: Settings,
    route: "/admin/settings",
    status: "available",
    category: "Sistema",
  },
  {
    title: "Comunicação Omnichannel",
    description: "Configuração de canais de envio como WhatsApp e e-mail para automações.",
    icon: MessageSquare,
    route: "/admin/communication",
    status: "available",
    category: "Sistema",
  },
  {
    title: "Chat WhatsApp",
    description: "Ambiente dedicado para operar conversas em tempo real usando Evolution API.",
    icon: MessageSquare,
    route: "/admin/whatsapp-chat",
    status: "available",
    category: "Sistema",
  },
  {
    title: "Auditoria de Ações",
    description: "Rastreabilidade de alterações críticas feitas no sistema.",
    icon: Shield,
    status: "planned",
    category: "Segurança",
  },
  {
    title: "Saúde de Integrações",
    description: "Visibilidade de jobs, filas e integrações externas.",
    icon: Database,
    status: "planned",
    category: "Sistema",
  },
  {
    title: "Central de Alertas",
    description: "Alertas globais com ranking de risco, anomalias e sinais de churn operacional.",
    icon: Bell,
    route: "/admin/dashboard",
    status: "available",
    category: "Operação",
  },
];

const categoryOrder: Array<ControlModule["category"]> = ["Operação", "Financeiro", "Segurança", "Sistema"];

export default function AdminControlCenter() {
  return (
    <AdminLayout>
      <div className="space-y-6">
        <PageHeaderCard
          eyebrow="Administração"
          title="Central de Controle"
          subtitle="Ponto único para gestão administrativa completa da plataforma."
          backTo={null}
        />

        {categoryOrder.map((category) => {
          const categoryModules = modules.filter((module) => module.category === category);

          return (
            <div key={category} className="space-y-3">
              <h2 className="text-lg font-semibold text-foreground">{category}</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {categoryModules.map((module) => {
                  const Icon = module.icon;

                  return (
                    <Card key={module.title} className="rounded-2xl border-border/60 shadow-sm">
                      <CardHeader className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center">
                            <Icon className="h-4 w-4 text-violet-600" />
                          </div>
                          <Badge
                            className={
                              module.status === "available"
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-amber-100 text-amber-800"
                            }
                          >
                            {module.status === "available" ? "Disponível" : "Planejado"}
                          </Badge>
                        </div>

                        <CardTitle className="text-base">{module.title}</CardTitle>
                        <CardDescription>{module.description}</CardDescription>
                      </CardHeader>

                      <CardContent>
                        {module.route ? (
                          <Button asChild className="w-full gap-2" variant="outline">
                            <Link to={module.route}>
                              Gerenciar
                              <ArrowRight className="h-4 w-4" />
                            </Link>
                          </Button>
                        ) : (
                          <Button className="w-full" variant="outline" disabled>
                            Em breve
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </AdminLayout>
  );
}
